import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, KeyRound, Mail, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { isValidPin } from '../../auth/pinMapping';
import Button from '../../components/Button';
import AuthMessage from '../../components/AuthMessage';
import loginImage from '../../assets/login image bhima.png';
import bhimaLogo from '../../assets/bhima-logo.svg';
import styles from '../../styles/authShell.module.css';

const STEPS = { EMAIL: 'email', RESET: 'reset' };

export default function ForgotPin() {
  const { forgotPassword, confirmForgotPassword } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.EMAIL);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmNewPin, setConfirmNewPin] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await forgotPassword(email);
      setStep(STEPS.RESET);
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!isValidPin(newPin)) {
      setError('PIN must be exactly 4 digits.');
      return;
    }
    if (newPin !== confirmNewPin) {
      setError('PINs do not match.');
      return;
    }
    setLoading(true);
    try {
      await confirmForgotPassword(email, code, newPin);
      navigate('/login?reset=1', { replace: true });
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.panel}>
        <img src={loginImage} alt="" className={styles.panelImage} />
      </div>

      <div className={styles.formSide}>
        {step === STEPS.EMAIL && (
          <form className={styles.form} onSubmit={handleEmailSubmit}>
            <img src={bhimaLogo} alt="Bhima Store" className={styles.mobileLogo} />
            <p className={styles.eyebrow}>Forgot your PIN?</p>
            <h2 className={styles.formTitle}>Reset your PIN</h2>
            <p className={styles.formSubtitle}>Enter your account email and we'll send you a reset code.</p>

            <AuthMessage>{error}</AuthMessage>

            <label className={styles.field}>
              <span>Email</span>
              <div className={styles.inputWrap}>
                <Mail size={15} />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>
            </label>

            <Button
              type="submit"
              size="lg"
              icon={ArrowRight}
              iconPosition="right"
              disabled={loading}
              className={styles.submit}
            >
              {loading ? 'Sending…' : 'Send reset code'}
            </Button>

            <p className={styles.helper}>
              Remembered it? <Link to="/login" className={styles.linkBtn}>Sign in</Link>
            </p>
          </form>
        )}

        {step === STEPS.RESET && (
          <form className={styles.form} onSubmit={handleResetSubmit}>
            <p className={styles.eyebrow}>Check your email</p>
            <h2 className={styles.formTitle}>Enter the code and a new PIN</h2>
            <p className={styles.formSubtitle}>We sent a reset code to {email}.</p>

            <AuthMessage>{error}</AuthMessage>

            <label className={styles.field}>
              <span>Reset code</span>
              <div className={styles.inputWrap}>
                <ShieldCheck size={15} />
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  required
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                />
              </div>
            </label>

            <label className={styles.field}>
              <span>New 4-digit PIN</span>
              <div className={styles.inputWrap}>
                <KeyRound size={15} />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  required
                  className={styles.pinInput}
                  value={newPin}
                  onChange={(e) => setNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••"
                />
              </div>
            </label>

            <label className={styles.field}>
              <span>Confirm new PIN</span>
              <div className={styles.inputWrap}>
                <KeyRound size={15} />
                <input
                  type="password"
                  inputMode="numeric"
                  maxLength={4}
                  required
                  className={styles.pinInput}
                  value={confirmNewPin}
                  onChange={(e) => setConfirmNewPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                  placeholder="••••"
                />
              </div>
            </label>

            <Button type="submit" size="lg" disabled={loading} className={styles.submit}>
              {loading ? 'Resetting…' : 'Reset PIN'}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
}
